import type { IUser, UserSettings } from '@carboncopyco/types';
import { Debug } from '@carboncopyco/utils';
import { mainApi } from './mainApi';

Debug.extend('demostack-data:api:currentUserApi');

export interface GetCurrentUserResponse {
  user?: IUser;
  isAuthenticated: boolean;
}

export interface UpdateCurrentUserArgs {
  email?: IUser['email'];
  firstName: IUser['firstName'];
  lastName: IUser['lastName'];
  image?: IUser['image'];
  hiddenMessages?: Record<string, any>;
}

export interface GetCurrentUserRolesResponse {
  createTime: string;
  email: string;
  id: number;
  isDeleted: boolean;
  organizationId: string;
  role: string;
  updateTime: string;
}

export interface GetUserSettingsArgs {
  organizationId: string;
}

export interface UpdateUserSettingsArgs extends UserSettings {
  organizationId: string;
}

export interface UserSettingsResponse extends UserSettings {
  id: string;
  userId: string;
  organizationId: string;
  createTime: string;
  updateTime: string;
  archiveTime?: string;
  isDeleted: boolean;
}

const currentUserApi = mainApi.injectEndpoints({
  endpoints: (builder) => ({
    getCurrentUser: builder.query<GetCurrentUserResponse, void>({
      queryFn: async (_, __, ___, baseQuery) => {
        const response = await baseQuery({
          url: '/api/user/me',
          method: 'GET',
        });

        if (response.data) {
          if ((response.data as unknown as { msg: string })?.msg) {
            return { data: { isAuthenticated: false } };
          }
          return {
            data: {
              user: response.data,
              isAuthenticated: true,
            },
          };
        }
        return response;
      },
      providesTags: ['User'],
    }),

    updateCurrentUser: builder.mutation<IUser, UpdateCurrentUserArgs>({
      query: (args) => ({
        url: '/api/user/me',
        method: 'POST',
        body: args,
      }),
      invalidatesTags: ['User'],
      onQueryStarted: async (
        { firstName, lastName, image },
        { dispatch, queryFulfilled }
      ) => {
        const patchResult = dispatch(
          currentUserApi.util.updateQueryData(
            'getCurrentUser',
            undefined,
            (authenticationData) => {
              if (authenticationData?.user) {
                authenticationData.user.firstName = firstName;
                authenticationData.user.lastName = lastName;
                authenticationData.user.image = image;
              }
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    getCurrentUserRoles: builder.query<GetCurrentUserRolesResponse, void>({
      query: () => ({
        url: '/api/user/me/roles',
        method: 'GET',
      }),
    }),

    logoutCurrentUser: builder.mutation<undefined, void>({
      query: () => ({
        url: '/api/logout',
        method: 'POST',
        body: { logout: true },
      }),
    }),

    getUserSettings: builder.query<UserSettingsResponse, GetUserSettingsArgs>({
      query: ({ organizationId }) => ({
        url: `api/${organizationId}/user/settings`,
        method: 'GET',
      }),
      providesTags: ['NotificationsSettings'],
    }),

    updateUserSettings: builder.mutation<
      UserSettingsResponse,
      UpdateUserSettingsArgs
    >({
      query: ({ organizationId, activityReport, emailNotification }) => ({
        url: `/api/${organizationId}/user/settings`,
        method: 'POST',
        body: {
          activityReport,
          emailNotification,
        },
      }),
      onQueryStarted: async (
        { organizationId, activityReport, emailNotification },
        { dispatch, queryFulfilled }
      ) => {
        const patchResult = dispatch(
          currentUserApi.util.updateQueryData(
            'getUserSettings',
            { organizationId: String(organizationId) },
            (draft) => {
              Object.assign(draft, {
                activityReport,
                emailNotification,
              });
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: ['NotificationsSettings'],
    }),
  }),
});

export const {
  useGetCurrentUserQuery,
  useGetUserSettingsQuery,
  useUpdateUserSettingsMutation,
  useUpdateCurrentUserMutation,
  useGetCurrentUserRolesQuery,
  useLogoutCurrentUserMutation,
} = currentUserApi;
